import { cloneDeep } from 'lodash';
import * as React from 'react';
import { useContextMenu } from '../../hooks/use-context-menu';
import { InsertMethod } from '../models/enums/insert-method';
import { ProductionRun } from '../models/schedule/production-run';
import { MetricBase } from '../models/shared/metric-base';
import { JobRightClickMenu } from './JobRightClickMenu';
import { MetricRow } from './MetricRow';
import { MetricTableHeader } from './MetricTableHeader';
import { MetricTableLegend } from './MetricTableLegend';
import { MetricsRightClickMenu } from './MetricsRightClickMenu';
import { RunRightClickMenu } from './RunRightClickMenu';

const ContextMenu = ({ menu }: any) => {
    const { xPos, yPos, showMenu } = useContextMenu(menu);
    return showMenu
        ? <div
            className="right-click menu-container"
            style={{
                top: yPos,
                left: xPos,
                opacity: 1
            }}>
            {menu}
        </div>
        : null;
};

interface IProps {
    runs: MetricBase[];
    cells?: any[];
    headers: string[];
    loadLabel?: string;
    // The runs have only the loading column of the metrics, as those of the Rolling Steel schedule do
    loadingOnly?: boolean;
    showLegend: boolean;
    onCellChange?: any;
    nonMetricPropCount: number;
    clearSelections?: any;
    completeSelections?: any;
    onRowsCut?: any;
    onRowInsert? : any;
    onlyRowSelect? : boolean;
    onCancelSelectedRows? : any;
    onCompleteSelectedRows? : any;
    printSelections? : any;
    downloadSelections?: any;
    onViewDetails?: any;
    onStartProduction? : any;
    onSetGrouping? : any;
    onPrintAllJobs?: any;
    onCompleteAllJobs?: any;
    onPrintPaperwork?: any;
}

/**
 * Select, or let go of, every cell of a row.  A row is the cells of its properties and then the cells of its metrics,
 * and how many there are is whatever the row has.
 */
const setRowSelected = (rowCells: any[], isSelected: boolean) => {
    rowCells.forEach((cell: any) => { cell.isCurrentlySelected = isSelected; });
};

// The rows of the header that come before the first run: the groups, the sub groups and the columns of the metrics, or
// only the columns when the loading column is the only metric
const HEADER_ROW_COUNT = 3;
const LOADING_ONLY_HEADER_ROW_COUNT = 1;

export const MetricTable = (props: IProps) => {

    const [selectionStarted, setSelectionStarted] = React.useState(false);
    const [startRow, setStartRow] = React.useState(0);
    const [startColumn, setStartColumn] = React.useState(0);
    const [endRow, setEndRow] = React.useState(0);
    const [endColumn, setEndColumn] = React.useState(0);
    const [addMode, setAddMode] = React.useState(false);
    const [fullRows, setFullRows] = React.useState<any[]>([]);
    const [hasCutRowsPending, setHasCutRowsPending] = React.useState(false);
    const [insertPoint, setInsertPoint] = React.useState(-1);
    const [hasSelections, setHasSelections] = React.useState(false);
            
    React.useEffect(() => {

        window.addEventListener("mouseup", handleTouchEndWindow);
        window.addEventListener("touchend", handleTouchEndWindow);

        return function cleanup() {
            window.removeEventListener("mouseup", handleTouchEndWindow);
            window.removeEventListener("touchend", handleTouchEndWindow);
        };
    });

    React.useEffect(() => {
        if (props.runs.length === 0) {
            setFullRows([]);
        }
    }, [props.runs]);

    /**
     * Display a right click menu based on the selections
     * @returns A right click menu to show
     */
    const getMenuContext = () => {

        if (!hasSelections) {
            return null;
        }
        else if (fullRows.length > 0 && props.onlyRowSelect) {            
            return <MetricsRightClickMenu
                clearSelections={ cancelSelectedRows }
                viewDetails={ props.onViewDetails }
                startProduction={ props.onStartProduction }
                setGrouping={ fullRows.length === 1 ? props.onSetGrouping : undefined }
                rowCount={ fullRows.length }
                heightOffset={ props.onSetGrouping && fullRows.length === 1 ? 190 : 150 }
            />;
        }
        else if (fullRows.length > 0 && !props.onlyRowSelect) {

            let height = fullRows.length === 1 ? 415 : 390;
            if (hasCutRowsPending) {
                height = 150;
            }

            return (
                <RunRightClickMenu
                    hasCutRowsPending={ hasCutRowsPending }
                    insertRows={ insertRows }
                    cancelSelectedRows={ cancelSelectedRows }
                    clearSelections={ clearSelections }
                    completeSelectedRows={ completeSelectedRows }
                    cutRows={ cutRows }
                    viewDetails={ props.onViewDetails }
                    rowCount={ fullRows.length }
                    heightOffset={ height }
                    insertPoint={ insertPoint }
                    printAllJobs={ printAllJobs }
                    completeAllSelections={ completeAllJobs }
                    printPaperwork={ props.onPrintPaperwork }
                />
            );
        }
        else if (!props.onlyRowSelect) {
            return <JobRightClickMenu
                completeSelections={ props.completeSelections }
                printJobs={ printJobs }
                downloadSelections={ downloadJobs }
                clearSelections={ clearSelections }
                heightOffset={ 250 }
            />;
        }
        else {
            return null;
        }

    };

    /**
     * Show the selected rows as being cut and fire the callback
     * to update the parent
     */
    const cutRows = () => {
        setHasCutRowsPending(true);
        props.onRowsCut(fullRows);
    };

    /**
     * Determine the before and after indices and the number of selected rows.
     * Call back to the parent so the lexo ranks can be updated
     * @param type a flag to decide if you're inserting above or below the insert point
     */
    const insertRows = (type : InsertMethod) => {

        // reorder cells
        let afterIndex : number|undefined;
        let beforeIndex : number|undefined;
        let insertQuantity : number|undefined;

        // copy our full row list
        const tmpFullRows = [...fullRows];

        // remove the insert point row id from full row list
        // insertRowId should be the after after point
        const insertRowId = tmpFullRows.findIndex((itm : any) => itm.runId === insertPoint);
        const insertIndex = tmpFullRows[insertRowId].index;
        
        // Calculate our Indices

        // Adding the new rows to the very top of the list
        if (insertIndex === 0 && type === InsertMethod.Above) {
            afterIndex = undefined;
            beforeIndex = insertIndex;
        }
        // Adding the new rows under the first item in the list
        else if (insertIndex === 0 && type === InsertMethod.Below) {
            afterIndex = insertIndex;
            beforeIndex = (insertIndex + 1) <= props.runs.length ? insertIndex + 1 : undefined;
        }
        // Adding the new rows above the last item in the list
        else if ((insertIndex === props.runs.length - 1) && type === InsertMethod.Above) {
            afterIndex = (insertIndex - 1) >= 0 ? insertIndex - 1 : undefined;
            beforeIndex = insertIndex;
        }
        // Adding the new rows below the last item in the list
        else if ((insertIndex === props.runs.length - 1) && type === InsertMethod.Below) {
            beforeIndex = undefined;
            afterIndex = insertIndex;
        }
        // Adding a row to the middle of the table above the selected
        else if (type === InsertMethod.Above) {
            beforeIndex = insertIndex;
            afterIndex = insertIndex - 1;
        }
        // Adding a row to the middle of the table below the selected
        else {
            beforeIndex = insertIndex + 1;
            afterIndex = insertIndex;
        }

        // tmpFullRows now represents how the rows we are selecting to move
        tmpFullRows.splice(insertRowId, 1);
        insertQuantity = tmpFullRows.length;

        const tmpCells = cloneDeep(props.cells);
        const pulledRowList = [];

        for (let i = 0; i < tmpFullRows.length; i++) {

            for (let j = 0; j < tmpCells!.length; j++) {

                if (tmpCells![j][0].parentRunId === tmpFullRows[i].runId) {
                    const removed = tmpCells!.splice(j, 1);
                    pulledRowList.push(...removed);
                    break;
                }
            }
        }

        setFullRows([]);
        setInsertPoint(-1);
        setHasCutRowsPending(false);

        tmpFullRows.sort((itmA: any, itmB: any) => {
            if (itmA.index < itmB.index) { return -1; }
            if (itmA.index > itmB.index) { return 1; }
            return 0;
        });

        props.onRowInsert(tmpFullRows, beforeIndex, afterIndex, insertQuantity, type);
    };

    /**
     * Clear out our selection state and fire the callback
     * to update the parent
     */
    const clearSelections = () => {
        setHasCutRowsPending(false);
        setFullRows([]);
        setInsertPoint(-1);
        setHasSelections(false);
        props.clearSelections();
    };

    /**
     * Call back to the parent to print the selected jobs
     */
    const printJobs = () => {
        setHasCutRowsPending(false);
        setFullRows([]);
        props.printSelections();
    };

    /**
     * Call back to the parent to print the selected jobs
     */
    const downloadJobs = () => {
        setHasCutRowsPending(false);
        setFullRows([]);
        props.downloadSelections();
    };

    /**
     * Fire a callback to cancel the selected runs
     */
    const cancelSelectedRows = () => {
        setHasCutRowsPending(false);

        const runIds = fullRows.reduce((idsToRemove : number[], run : any) => {
            idsToRemove.push(run.runId);
            return idsToRemove;
        }, []);

        setFullRows([]);
        props.onCancelSelectedRows(runIds);
    };

    /**
     * Fire a callback to complete the selected runs
     */
    const completeSelectedRows = () => {
        setHasCutRowsPending(false);

        const runIds = fullRows.reduce((idsToRemove : number[], run : any) => {
            idsToRemove.push(run.runId);
            return idsToRemove;
        }, []);

        setFullRows([]);
        props.onCompleteSelectedRows(runIds);
    };


    /**
     * Fire a callback to print all the jobs selected in full rows
     */
    const printAllJobs = () => {    
        const runIds = fullRows.reduce((idsToPrint : number[], run : any) => {
            idsToPrint.push(run.runId);
            return idsToPrint;
        }, []);

        setFullRows([]);
        props.onPrintAllJobs(runIds);
    };

    /**
     * Fire a callback to complete all the jobs selected in full rows
     */
    const completeAllJobs = () => {    
        const runIds = fullRows.reduce((idsToComplete : number[], run : any) => {
            idsToComplete.push(run.runId);
            return idsToComplete;
        }, []);

        setFullRows([]);
        props.onCompleteAllJobs(runIds);
    };

    const eventToCellLocation = (e: any) => {
        let target;
        // For touchmove and touchend events, e.target and e.touches[n].target are
        // wrong, so we have to rely on elementFromPoint(). For mouse clicks, we have
        // to use e.target.
        if (e.touches) {
            const touch = e.touches[0];
            target = document.elementFromPoint(touch.clientX, touch.clientY);
        } else {
            target = e.target;
            while (target.tagName !== "TD") {
                target = target.parentNode;
            }
        }
        return {
            row: target.parentNode.rowIndex - (props.loadingOnly ? LOADING_ONLY_HEADER_ROW_COUNT : HEADER_ROW_COUNT),
            // column: target.cellIndex - 4
            column: target.cellIndex
        };
    };

    const handleTouchStartCell = (e: any) => {
        const isLeftClick = e.button === 0;
        const isTouch = e.type !== "mousedown";
        if (!selectionStarted && (isLeftClick || isTouch)) {

            e.preventDefault();
            const { row, column } = eventToCellLocation(e);

            // TODO: Should I fix this?
            // this.props.onSelectionStart({ row, column });

            setSelectionStarted(true);
            setStartRow(row);
            setStartColumn(column);
            setEndRow(row);
            setEndColumn(column);
            setAddMode(!props.cells![row][column].isCurrentlySelected);
        }
    };

    const handleTouchMoveCell = (e: any) => {
        if (selectionStarted) {
            e.preventDefault();
            const { row, column } = eventToCellLocation(e);

            if (endRow !== row || endColumn !== column) {
                const nextRowCount =
                    startRow === null && endRow === null
                        ? 0
                        : Math.abs(row - startRow) + 1;
                const nextColumnCount =
                    startColumn === null && endColumn === null
                        ? 0
                        : Math.abs(column - startColumn) + 1;

                if (nextRowCount <= 1000) {
                    setEndRow(row);
                }

                if (nextColumnCount <= 40) {
                    setEndColumn(column);
                }
            }
        }
    };

    const handleTouchEndWindow = (e: any) => {
        const isLeftClick = e.button === 0;
        const isTouch = e.type !== "mousedown";
        if (selectionStarted && (isLeftClick || isTouch)) {

            const tmpCells = cloneDeep(props.cells);
            const minRow = Math.min(startRow, endRow);
            let maxRow = Math.max(startRow, endRow);

            let fRows = [...fullRows];
            let overrideAddMode = false;

            const minColumn = Math.min(startColumn, endColumn);
            const maxColumn = Math.max(startColumn, endColumn);

            if (props.onlyRowSelect) {
                console.log('Only selecting rows, just skip resetting state');
            }
            // Selection started and ended in the metrics cells and there were already full rows selected
            // Need to clear full rows and set a boolean to force selection
            else if (minColumn >= props.nonMetricPropCount && maxColumn >= props.nonMetricPropCount && fullRows.length > 0) {

                overrideAddMode = true;

                for (let i = 0; i < props.runs.length; i++) {
                    setRowSelected(tmpCells![i], false);
                }

                // setFullRows([]);
                fRows = [];
            }
            // Selection started and ended in the properties cells and we didn't have rows already selected
            // Need to clear cells to wipe out any metric selections and turn on force selection to select that row
            else if ((minColumn < props.nonMetricPropCount) && (maxColumn < props.nonMetricPropCount) && fullRows.length === 0) {
                
                overrideAddMode = true;

                for (let i = 0; i < props.runs.length; i++) {
                    setRowSelected(tmpCells![i], false);
                }

                fRows = [];
            }

            // This is a check to only select a single row if you have cut rows pending
            // The assumption is that if you have cut rows pending, you should only be selecting
            // a single insert point.  Clicking and dragging while trying to set an insert point
            // is invalid.
            if (hasCutRowsPending) {
                maxRow = minRow;
            }

            for (let row = minRow; row <= maxRow; row++) {

                if (hasCutRowsPending && insertPoint === -1) {                
                    setInsertPoint(tmpCells![row][0].parentRunId);
                }
                else if (hasCutRowsPending && insertPoint !== -1) {
                    for (let i = 0; i < props.runs.length; i++) {

                        if (props.runs[i].METRIC_LIST[0].parentRunId === insertPoint) {

                            setRowSelected(tmpCells![i], false);

                            break;
                        }
                    }

                    // We're deselecting the insertPoint so we want to turn off that 
                    // one row
                    if (tmpCells![row][0].parentRunId === insertPoint) {
                        setInsertPoint(-1);
                    }
                    else {
                        setInsertPoint(tmpCells![row][0].parentRunId);
                    }
                }

                let skipSelection = false;
                let trueMaxColumn = maxColumn;
                let trueMinColumn = minColumn;

                // Selection started and edned within the "properties" cells
                // Select the whole row
                if (((minColumn < props.nonMetricPropCount) && (maxColumn < props.nonMetricPropCount)) || props.onlyRowSelect) {
                    trueMaxColumn = tmpCells![row].length - 1;
                    trueMinColumn = 0;

                    if (addMode) {
                        const r = {
                            index: row,
                            runId: tmpCells![row][0].parentRunId
                        };

                        fRows.push(r);
                    }
                    else {
                        const findex = fRows.findIndex((tim: any) => tim.runId === tmpCells![row][0].parentRunId);                        
                        fRows.splice(findex, 1);
                    }
                }
                // Selection started in the "properties" cells and ended in metrics or vice versa
                // Select nothing
                else if (((minColumn < props.nonMetricPropCount) && (maxColumn >= props.nonMetricPropCount)) ||
                    ((minColumn >= props.nonMetricPropCount) && (maxColumn < props.nonMetricPropCount))) {
                    skipSelection = true;
                }
                
                if (!skipSelection) {
                    for (let column = trueMinColumn; column <= trueMaxColumn; column++) {
                        tmpCells![row][column].isCurrentlySelected = overrideAddMode === true ? overrideAddMode : addMode;
                    }
                }
            }

            if (overrideAddMode === true) {
                setAddMode(true);
            }

            setSelectionStarted(false);
            setFullRows(fRows);

            if (props.onCellChange) {

                if (tmpCells) {

                    // Before we event back up, check for something being selected
                    // so we have an easily accessible way to know if the right 
                    // click menu can be shown
                    const hadSelection = tmpCells.some((rowCells: any[]) =>
                        rowCells.some((cell: any) => cell.isCurrentlySelected === true));

                    setHasSelections(hadSelection);
                }

                props.onCellChange(tmpCells, fRows);
            }
        }
    };

    const isCellBeingSelected = (row: any, column: any) => {
        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);
        const minColumn = Math.min(startColumn, endColumn);
        const maxColumn = Math.max(startColumn, endColumn);

        const ret = (
            selectionStarted &&
            (row >= minRow &&
                row <= maxRow &&
                column >= minColumn &&
                column <= maxColumn)
        );

        return ret;
    };

    const isCellSelected = (row: any, column: any) => {
        return props.cells && props.cells.length > 0 && props.cells[row][column].isCurrentlySelected;
    };

    return (
        <div className="huge-table-wrap">

            {
                props.showLegend
                    ? <MetricTableLegend />
                    : null
            }

            <div className="row">
                <div className="col">
                    <table className={ `table hover prod-schedule ${ props.loadingOnly ? 'loading-only' : '' }` }>
                        <MetricTableHeader headers={ props.headers } loadLabel={ props.loadLabel } loadingOnly={ props.loadingOnly } />
                        <tbody>
                            {
                                props.runs && props.runs.length > 0 && props.cells
                                    ? props.runs.map((itm: ProductionRun, idx: number) =>
                                        <MetricRow
                                            run={itm}
                                            fullRowSelected={ itm.isChecked }
                                            cells={props.cells![idx]}
                                            key={`mr-${itm.tmpClientId}-${idx}`}
                                            nonMetricPropCount={ props.nonMetricPropCount }
                                            readonly={false}
                                            index={idx}
                                            metricBeingSelected={isCellBeingSelected}
                                            onTouchStart={handleTouchStartCell}
                                            onTouchMove={handleTouchMoveCell}
                                            isCellSelected={isCellSelected}
                                            showOnlyQuantites={ props.onlyRowSelect }
                                        />
                                    )
                                    : <tr><td className="no-metrics-to-show" colSpan={50}>No Data Found</td></tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>

            <ContextMenu menu={ getMenuContext() } />
            
        </div>

    );
};
